import { displayValueOrNA } from './displayValueOrNA';

import type { CurrencyHandler } from '@/hooks/useCurrency';
import type { CandidateInfo } from '@/types/candidate';
import type { SalaryFeeBracket } from '@/types/company';
import type { Job } from '@/types/job';
import type { RecruiterFeeType } from '@/types/recruiter';
import type { TFunction } from 'i18next';

/**
 * Recruiter-visible share of compensation used in fee summary display.
 * Product/commercial rule — confirm with product before changing.
 */
const RECRUITER_FEE_DISPLAY_SHARE = 0.7;

const parseNumericFee = (
  value: string | number | null | undefined,
): number | null => {
  if (value == null || value === '') {
    return null;
  }
  const parsed = Number(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const calculateRecruiterFee = (
  compensationDetails: number,
  type: 'percentage' | 'fixed',
): number => {
  let fee: number;

  if (type === 'percentage') {
    fee = compensationDetails * RECRUITER_FEE_DISPLAY_SHARE;
    fee = parseFloat(fee.toFixed(1));
  } else {
    fee = Math.floor(compensationDetails * RECRUITER_FEE_DISPLAY_SHARE);
  }

  return fee;
};

const formatFeeValue = (value: number): string => {
  const rounded = parseFloat(value.toFixed(2));
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
};

const formatSalaryBracketValue = (
  currency: CurrencyHandler,
  value: number,
): string => currency.curr(value);

const getSalaryFeeBracketRange = (
  salaryFeeBrackets?: SalaryFeeBracket[] | null,
) => {
  if (!salaryFeeBrackets?.length) {
    return null;
  }

  const feePercentages = salaryFeeBrackets
    .map((bracket) => bracket.fee_percentage)
    .filter(
      (feePercentage): feePercentage is number =>
        typeof feePercentage === 'number',
    );

  if (!feePercentages.length) {
    return null;
  }

  return {
    min: Math.min(...feePercentages),
    max: Math.max(...feePercentages),
  };
};

export const getSalaryFeeBracketDetails = (
  t: TFunction,
  currency: CurrencyHandler,
  salaryFeeBrackets?: SalaryFeeBracket[] | null,
): string[] => {
  if (!salaryFeeBrackets?.length) {
    return [];
  }

  return [...salaryFeeBrackets]
    .sort((left, right) => left.min_salary - right.min_salary)
    .map((bracket) => {
      const from = formatSalaryBracketValue(currency, bracket.min_salary);
      const range =
        bracket.max_salary == null
          ? `${from}+`
          : `${from} - ${formatSalaryBracketValue(currency, bracket.max_salary)}`;

      return `${range}: ${formatFeeValue(bracket.fee_percentage)}% ${t('OF_YEARLY_SALARY')}`;
    });
};

export type RecruiterFeeSummarySubject = Partial<
  Pick<
    Job,
    | 'company_agreement'
    | 'recruiter_company_agreement'
    | 'recruiter_compensation_type'
    | 'recruiter_compensation_type_tooltip'
    | 'employment_type'
    | 'earn_amount_2'
  >
> &
  Partial<
    Pick<
      CandidateInfo,
      'et_details_freelance' | 'et_details_ctp' | 'et_gda_ctp' | 'et_cao_ctp'
    >
  >;

const getRecruiterFeeSummary = (
  t: TFunction,
  defaultCurrency: CurrencyHandler,
  job: RecruiterFeeSummarySubject,
  isClient?: boolean,
) => {
  // use the details in a recruiter/company agreement, if present
  // then the details in the job flex fee, if present.
  const agreement = Boolean(
    job.company_agreement?.company || job.recruiter_company_agreement?.company,
  );
  const salaryFeeBracketRange = !isClient
    ? getSalaryFeeBracketRange(
        job.company_agreement?.salary_fee_brackets ||
          job.recruiter_company_agreement?.salary_fee_brackets,
      )
    : null;
  const employmentType = job.employment_type?.type.id || '';
  const employmentInfo = (() => {
    if (['freelance', 'ctp'].includes(employmentType)) {
      return {
        compensationType: 'custom',
        compensationDetails:
          employmentType === 'freelance'
            ? job.company_agreement?.et_details_freelance ||
              job.recruiter_company_agreement?.et_details_freelance ||
              job.et_details_freelance
            : job.company_agreement?.et_details_ctp ||
              job.recruiter_company_agreement?.et_details_ctp ||
              job.et_details_ctp ||
              job.et_gda_ctp ||
              job.et_cao_ctp,
      } as { compensationType: 'custom'; compensationDetails: string };
    }
    return {
      compensationType:
        job.company_agreement?.compensation_type ||
        job.recruiter_company_agreement?.compensation_type ||
        job.recruiter_compensation_type ||
        '',
      compensationDetails:
        job.company_agreement?.compensation_type_tooltip ||
        job.recruiter_company_agreement?.compensation_type_tooltip ||
        job.recruiter_compensation_type_tooltip,
    } as {
      compensationType: RecruiterFeeType;
      compensationDetails: string | number | null | undefined;
    };
  })();

  const hasHireportFee =
    !job.company_agreement?.compensation_type &&
    !job.recruiter_company_agreement?.compensation_type;

  const feeCurrency = agreement
    ? job.company_agreement?.fee_currency ||
      job.recruiter_company_agreement?.fee_currency
    : null;

  const currency = feeCurrency
    ? defaultCurrency.changeCurrency(feeCurrency)
    : defaultCurrency;

  if (salaryFeeBracketRange) {
    return {
      recruiterFee: `${formatFeeValue(salaryFeeBracketRange.min)}%-${formatFeeValue(salaryFeeBracketRange.max)}% ${t('OF_YEARLY_SALARY')}`,
      fallbackActive: false,
      feeUnknown: false,
      hasHireportFee,
      currency,
    };
  }

  switch (employmentInfo.compensationType) {
    case 'custom':
      return {
        recruiterFee: `${t('COMPENSATION_TYPE_CUSTOM')}: ${displayValueOrNA(employmentInfo.compensationDetails, t)}`,
        fallbackActive: false,
        feeUnknown: false,
        hasHireportFee,
        currency,
      };
    case 'percentage': {
      const feeAmount = parseNumericFee(employmentInfo.compensationDetails);
      if (feeAmount == null) {
        return {
          recruiterFee: displayValueOrNA(employmentInfo.compensationDetails, t),
          fallbackActive: false,
          feeUnknown: false,
          hasHireportFee,
          currency,
        };
      }
      return {
        recruiterFee:
          isClient || agreement
            ? `${feeAmount}% ${t('OF_YEARLY_SALARY')}`
            : `${calculateRecruiterFee(feeAmount, 'percentage')}% ${t('OF_YEARLY_SALARY')}`,
        fallbackActive: false,
        feeUnknown: false,
        hasHireportFee,
        currency,
      };
    }
    case 'up to': {
      const feeAmount = parseNumericFee(employmentInfo.compensationDetails);
      if (feeAmount == null) {
        return {
          recruiterFee: displayValueOrNA(employmentInfo.compensationDetails, t),
          fallbackActive: false,
          feeUnknown: false,
          hasHireportFee,
          currency,
        };
      }
      return {
        recruiterFee:
          isClient || agreement
            ? `${t('COMPENSATION_TYPE_UP_TO')} ${currency.curr(feeAmount)}`
            : `${t('COMPENSATION_TYPE_UP_TO')} ${currency.curr(
                calculateRecruiterFee(feeAmount, 'fixed'),
              )}`,
        fallbackActive: false,
        feeUnknown: false,
        hasHireportFee,
        currency,
      };
    }
    case 'fixed': {
      const feeAmount = parseNumericFee(employmentInfo.compensationDetails);
      if (feeAmount == null) {
        return {
          recruiterFee: displayValueOrNA(employmentInfo.compensationDetails, t),
          fallbackActive: false,
          feeUnknown: false,
          hasHireportFee,
          currency,
        };
      }
      return {
        recruiterFee:
          isClient || agreement
            ? currency.curr(feeAmount)
            : `${t('FEE_TYPE_FIXED')}: ${currency.curr(
                calculateRecruiterFee(feeAmount, 'fixed'),
              )}`,
        fallbackActive: false,
        feeUnknown: false,
        hasHireportFee,
        currency,
      };
    }
  }

  // No compensation type matched — show translated N/A (do not use "-" sentinel; see feeUnknown for tiered-fee UI)
  return {
    compensationType: '',
    recruiterFee: displayValueOrNA(null, t),
    fallbackActive: true,
    feeUnknown: true,
    hasHireportFee,
    currency,
  };
};

export default getRecruiterFeeSummary;
